import React, { useCallback, useEffect, useReducer } from 'react';
import { TimePlannerReducer } from './reducers';
import { timePlannerDefaultState } from './state';
import { getWeekDay } from '../../utils/dateUtil';
import { TimePlannerActions, TimePlannerActionType } from './types';
import { getIntervalEntry } from './actions';
import { ApptTemplate } from '../../types/apptTemplate';
import { timePlannerHasStaffOnlyItems } from './timePlannerUtils';
import { getDaySlotsByHour } from '../../utils/appointmentUtil';
import { useActiveSiteApptLength } from '../../hooks/site/useActiveSiteApptLength';

export const useTimePlanner = ({
  selectedDate,
  apptTemplate,
  defaultTemplate,
  setShowOnline,
  showOnline,
  openHourInSeconds,
  closeHourInSeconds,
}: {
  defaultTemplate?: ApptTemplate;
  apptTemplate?: ApptTemplate;
  openHourInSeconds: number;
  closeHourInSeconds: number;
  selectedDate: Date;
  setShowOnline: React.Dispatch<React.SetStateAction<boolean>>;
  showOnline: boolean;
}) => {
  const dayNumber = getWeekDay(selectedDate);
  const renderIdRef = React.useRef(0);

  const [state, dispatch] = useReducer(TimePlannerReducer, {
    ...timePlannerDefaultState({
      openHourInSeconds,
      closeHourInSeconds,
    }),
  });
  const hoursLength = closeHourInSeconds - openHourInSeconds;

  const selectedTemplate = apptTemplate || defaultTemplate;

  const apptLength = useActiveSiteApptLength();
  const resetTimePlannerToDefault = useCallback(() => {
    defaultTemplate &&
      dispatch(
        getSetDefaultAction(
          {
            closeHourInSeconds,
            openHourInSeconds,
            setShowOnline,
            selectedDate,
            showOnline,
            dayNumber,
            hoursLength,
            apptLength,
          },
          defaultTemplate,
        ),
      );
  }, [
    dayNumber,
    hoursLength,
    showOnline,
    defaultTemplate,
    closeHourInSeconds,
    openHourInSeconds,
    setShowOnline,
    selectedDate,
    apptLength,
  ]);

  const resetTimePlanner = useCallback(() => {
    if (selectedTemplate) {
      dispatch(
        getSetDefaultAction(
          {
            closeHourInSeconds,
            openHourInSeconds,
            setShowOnline,
            selectedDate,
            showOnline,
            dayNumber,
            hoursLength,
            apptLength,
          },
          selectedTemplate,
        ),
      );
    } else {
      // No template selected. This can happen temporarily when changing site.
    }
  }, [
    selectedTemplate,
    showOnline,
    setShowOnline,
    openHourInSeconds,
    closeHourInSeconds,
    dayNumber,
    selectedDate,
    hoursLength,
    apptLength,
  ]);

  useEffect(() => {
    if (renderIdRef.current > 1) {
      dispatch({
        type: TimePlannerActionType.SET_ONLINE_ACTION,
      });
    }
    renderIdRef.current++;
  }, [showOnline]);

  useEffect(() => {
    resetTimePlanner();
  }, [resetTimePlanner]);
  return {
    dispatch,
    state,
    resetTimePlanner,
    resetTimePlannerToDefault,
  };
};

const getSetDefaultAction = (
  {
    selectedDate,
    setShowOnline,
    openHourInSeconds,
    closeHourInSeconds,
    dayNumber,
    apptLength,
  }: {
    openHourInSeconds: number;
    closeHourInSeconds: number;
    selectedDate: Date;
    setShowOnline: React.Dispatch<React.SetStateAction<boolean>>;
    showOnline: boolean;
    dayNumber: number;
    hoursLength: number;
    apptLength: number;
  },
  selectedTemplate: ApptTemplate,
): TimePlannerActions => {
  const isShowOnline = timePlannerHasStaffOnlyItems(selectedTemplate);
  if (isShowOnline) setShowOnline(true);
  return {
    type: TimePlannerActionType.SET_DEFAULT_STATE,
    payload: {
      closeHourInSeconds: closeHourInSeconds,
      openHourInSeconds: openHourInSeconds,
      timeInterval: apptLength,
      day: {
        total_planned: 0,
        staff_only_planned: 0,
        dayNumber: dayNumber,
        selected_date: selectedDate,
        hours: getDaySlotsByHour({
          openHourInSeconds,
          closeHourInSeconds,
          slotLengthInMinutes: apptLength,
        }).map(({ hour, minutes }) => {
          const intervals = minutes.map((minute) => {
            const intervalEntry = getIntervalEntry({
              timeTemplate: selectedTemplate,
              hourIndex: hour,
              minutes: minute,
            });
            return {
              total_planned: intervalEntry?.total_planned || 0,
              staff_only_planned: intervalEntry?.staff_only_planned || 0,
              minute: minute,
            };
          });

          return {
            hourNumber: hour,
            total_planned: 0,
            staff_only_planned: 0,
            intervals: intervals,
          };
        }),
      },
    },
  };
};
